import { inject, Injectable } from '@angular/core';
import { CountryCode, isPossiblePhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import toPath from 'lodash-es/toPath';

import {
  AggregateFunctionName,
  ComparisonOperator,
  ComparisonQueryNode,
  LogicalPredicateNode,
  LogicalQueryNode,
  LogicalType,
  PredicateType,
  QueryNodeType,
  SimplePredicateNode,
  VariableInfo,
  WorkflowPredicate,
} from '@blaze-shared/api-contracts-domain';
import {
  comparisonOperatorsWithNoValue,
  VisibilityExpressionState,
} from '@blaze-shared/datasets-domain';
import { ExpressionObj, RuntimeContext } from '@blaze-shared/elements-domain';
import { isFormula } from '@blaze-shared/formulas-domain-language-grammar';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  DatasetColumn,
  isDatasetLinkValue,
  isDatasetLinkValueOrArrayOfLinkValues,
  isLinkedInlineColumnAsLink,
  isTagId,
} from '@blaze-shared/schema-domain';
import {
  createGeoPointFromString,
  formatDurationForSaving,
  isDurationInSaveFormat,
  isGeoPoint,
} from '@blaze-shared/shared-feature';
import {
  deepClone,
  isDateOrDateTimeString,
  isNullish,
  isPrimitive,
  normalizeQuotes,
  regexpGetObjectKey,
  userBrowserCountry,
} from '@blaze-shared/shared-util';

import { evaluateExpression } from '../utils/intelli-expressions.utils';

import { AggregateFunctionWithGroup, Condition } from './models/intellisense';
import { IntellisenseErrorMessage } from './models/intellisense-error-messages';

@Injectable({ providedIn: 'root' })
export class IntelliExpressionsService {
  private readonly schemaService = inject(SchemaService);

  private readonly operators = [
    '!==',
    '>=',
    '<=',
    '===',
    '=',
    '<',
    '>',
    'ContainsText',
    'Eq',
    'EqIgnoreCase',
    'Gt',
    'Gte',
    'HasPrefix',
    'HasPrefixIgnoreCase',
    'In',
    'IsFalse',
    'IsNotNull',
    'IsNull',
    'IsTrue',
    'Lt',
    'Lte',
    'Near',
    'Neq',
    'NotIn',
  ].sort((a, b) => (a.length >= b.length ? -1 : 1));

  private readonly operatorsRx =
    /(!==|>=|<=|===|<|>|ContainsText|EqIgnoreCase|Gt|Gte|HasPrefixIgnoreCase|HasPrefix|IsTrue|IsFalse|In|IsNotNull|IsNull|Lt|Lte|Near|Neq|NotIn)/gi;
  private readonly expressionOperatorsRx = /(&&|\|\|)/gi;
  private readonly unaryOperators: ComparisonOperator[] = [
    ComparisonOperator.IsNull,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsTrue,
    ComparisonOperator.IsFalse,
  ];

  canEvaluate(expression: string): boolean {
    return (
      typeof expression === 'string' &&
      (expression.indexOf('$$') >= 0 || expression?.indexOf('@@') >= 0)
    );
  }

  createAggregateFunctions(
    functionsExpr: string[],
    groupByColumns: string[],
  ): AggregateFunctionWithGroup[] {
    const functions: AggregateFunctionWithGroup[] = [];
    functionsExpr.forEach((expr) =>
      functions.push(this.createAggregateFunction(expr, groupByColumns)),
    );
    return functions;
  }

  createAggregateFunction(
    fnExpr: string,
    groupByColumns: string[] = [],
  ): AggregateFunctionWithGroup {
    const fn = {
      groupBy: groupByColumns,
      alias: '',
    } as AggregateFunctionWithGroup;

    const fnName = this.getAggregateFunctionName(fnExpr);
    if (!fnName) {
      throw new Error(`${IntellisenseErrorMessage.InvalidAggregateFunction}: ${fnExpr}`);
    }
    fn.name = fnName;

    if (fn.name?.toLowerCase() !== 'count') {
      const colRegexp = /\(([^()]+)\)/;
      const colMatch = fnExpr.match(colRegexp);
      if (!colMatch?.[1]) {
        throw new Error(`${IntellisenseErrorMessage.AggregateNoColumnSpecified}: ${fnExpr}`);
      }
      fn.column = colMatch[1];
    } else {
      fn.column = '';
    }
    return fn;
  }

  createDatasetFilter(
    query: LogicalQueryNode | undefined,
    queryConditions: WorkflowPredicate[],
    condition: string,
    queryLogicalOperator: LogicalType | undefined,
    context: RuntimeContext,
  ): LogicalQueryNode {
    if (query) {
      return this.createDatasetFilterFromLogicalQueryNode(query, context);
    }

    const logicalType: LogicalType =
      queryLogicalOperator === 'Or' ? LogicalType.Or : LogicalType.And;

    if (queryConditions?.length) {
      return this.createDatasetFilterFromQueryConditions(queryConditions, logicalType, context);
    }

    if (condition) {
      return this.createDatasetFilterFromCondition(condition, logicalType, context);
    }

    return {
      nodeType: QueryNodeType.Logical,
      children: [],
      logicalType: LogicalType.And,
    };
  }

  createDatasetQuery(
    query: LogicalQueryNode | undefined,
    queryConditions: WorkflowPredicate[],
    condition: string,
    queryLogicalOperator: LogicalType | undefined,
    context: RuntimeContext,
  ): LogicalPredicateNode {
    if (query) {
      return this.createDatasetQueryFromLogicalQueryNode(query, context);
    }

    const logicalType: LogicalType =
      queryLogicalOperator === 'Or' ? LogicalType.Or : LogicalType.And;

    if (queryConditions?.length) {
      return this.createDatasetQueryFromQueryConditions(queryConditions, logicalType, context);
    }

    if (condition) {
      return this.createDatasetQueryFromCondition(condition, logicalType, context);
    }

    return {
      nodeType: PredicateType.Logical,
      children: [],
      logicalType: LogicalType.And,
    };
  }

  /**
   * Splits conditions string on the AND operator to return a list of filter conditions which need to be applied
   * on the data.
   * @param conditions - ie. input1.NumProjects >= input2.numProjects AND input1.Name != input2.name
   */
  createConditions(conditionsExpr: string): Condition[] {
    conditionsExpr = normalizeQuotes(conditionsExpr);
    if (!conditionsExpr) {
      return [];
    }
    const conditions: Condition[] = [];
    const conditionsList = this.getIndividualExpressions(conditionsExpr);
    conditionsList.forEach((conditionExpr) => {
      const condition = this.createCondition(conditionExpr);
      if (condition) {
        conditions.push(condition);
      }
    });
    return conditions;
  }

  /**
   * Splits string condition into three parts: keys used to look up InputA value, keys used to look up InputB value
   * and the comparator operator.
   * @param condition - string represention of the condition (ie. 'input1.NumProjects >= input2.numProjects')
   * @returns
   */
  createCondition(conditionExpr: string): Condition | null {
    // Order of operators in this list matters. Operators with largest amount of characters should be parsed first.
    const operator = this.operators.find(
      (supportedOperator) => conditionExpr.indexOf(supportedOperator) >= 0,
    );

    if (!operator) {
      console.error(`Invalid condition: ${conditionExpr}. Supported operator not found.`);
      return null;
    }

    let input1Path;
    let input2Path;
    let input1Keys;
    let input2Keys;
    const parts = conditionExpr.trim().split(operator);
    if (parts?.[0]?.indexOf('$$input1') >= 0 && parts?.[1]?.indexOf('$$input2') >= 0) {
      // ie. input1.ID = input2.ID
      if (parts[0]?.indexOf('.') >= 0) {
        input1Path = parts[0].split('.');
        input1Keys = input1Path.splice(1, parts[0].length).map((key) => key.trim());
      } else {
        input1Path = ['$$input1'];
        input1Keys = [regexpGetObjectKey(parts[0]) || ''];
      }
      if (parts[1]?.indexOf('.') >= 0) {
        input2Path = parts[1].split('.');
        input2Keys = input2Path.splice(1, parts[1].length).map((key) => key.trim());
      } else {
        input2Path = ['$$input2'];
        input2Keys = [regexpGetObjectKey(parts[1]) || ''];
      }
      return {
        input1: { keys: input1Keys, name: input1Path[0].trim(), value: '' },
        input2: { keys: input2Keys, name: input2Path[0].trim(), value: '' },
        operator,
      };
    } else if (parts[0].indexOf('$$input1') >= 0 && parts[1].indexOf('$$input2') < 0) {
      // ie. input1.ID = "abc123"
      if (parts[0]?.indexOf('.') >= 0) {
        input1Path = parts[0].split('.');
        input1Keys = input1Path.splice(1, parts[0].length).map((key) => key.trim());
      } else {
        input1Path = ['$$input1'];
        input1Keys = [regexpGetObjectKey(parts[0]) || ''];
      }
      const input1Value = this.parseExpressionValue(parts[1]);
      return {
        input1: { keys: input1Keys, name: input1Path[0].trim(), value: input1Value },
        input2: { keys: [], name: '', value: '' },
        operator,
      };
    } else if (parts[0].indexOf('$$input2') >= 0 && parts[1].indexOf('$$input1') >= 0) {
      // ie. input2.ID = input1.ID
      if (parts[0]?.indexOf('.') >= 0) {
        input1Path = parts[1].split('.');
        input1Keys = input1Path.splice(1, parts[1].length).map((key) => key.trim());
      } else {
        input1Path = ['$$input2'];
        input1Keys = [regexpGetObjectKey(parts[1]) || ''];
      }
      if (parts[1]?.indexOf('.') >= 0) {
        input2Path = parts[0].split('.');
        input2Keys = input2Path.splice(1, parts[0].length).map((key) => key.trim());
      } else {
        input2Path = ['$$input1'];
        input2Keys = [regexpGetObjectKey(parts[0]) || ''];
      }
      return {
        input1: { keys: input1Keys, name: input1Path[0].trim(), value: '' },
        input2: { keys: input2Keys, name: input2Path[0].trim(), value: '' },
        operator,
      };
    } else if (parts[0].indexOf('$$input2') >= 0 && parts[1].indexOf('$$input2') < 0) {
      // ie. input2.ID = "abc123"
      if (parts[1]?.indexOf('.') >= 0) {
        input2Path = parts[0].split('.');
        input2Keys = input2Path.splice(1, parts[0].length).map((key) => key.trim());
      } else {
        input2Path = ['$$input2'];
        input2Keys = [regexpGetObjectKey(parts[0]) || ''];
      }
      const input2Value = this.parseExpressionValue(parts[1]);
      return {
        input1: { keys: [], name: '', value: '' },
        input2: { keys: input2Keys, name: input2Path[0].trim(), value: input2Value },
        operator,
      };
    }
    return null;
  }

  async evalAsync(
    expression: string | any,
    context: RuntimeContext,
    ignoreNormalization = false,
  ): Promise<any> {
    const evaluatedResult = this.eval(expression, context, ignoreNormalization);
    const resultPromise =
      evaluatedResult instanceof Promise ? evaluatedResult : Promise.resolve(evaluatedResult);
    return await resultPromise.catch((err) => {
      console.warn(
        `Error parsing expression[${String(expression)}].`,
        'message' in err ? err.message : err,
      );
      return undefined;
    });
  }

  /**
   * Safely evaluates a javascript expression. Uses inputs as the context to evaluate the expression with params.
   * @param ignoreNormalization turns off linked params normalization - useful when eval is called in batches with the same context
   */
  eval(expression: string | any, context: RuntimeContext, ignoreNormalization = false): any {
    const originalExpression = expression;
    if (!expression) {
      return expression;
    }

    // Check whether expression is in the form of serialized JSON. Ignore simple strings
    if (expression[0] !== '"' && expression[expression.length - 1] !== '"') {
      try {
        expression = JSON.parse(expression);
      } catch {
        // We're not dealing with serialized expression. Proceed further.
      }
    }

    if (typeof expression !== 'string') {
      return this.evalAny(expression, context);
    }

    // Datetime strings should be returned as is.
    if (isDateOrDateTimeString(expression)) {
      return expression;
    }

    if (!ignoreNormalization) {
      const original = context;
      context = this.normalizeLinkedParams(context);
      // Keep copy of the original context (before normalization) in case there's a need to access display values of links
      context['$$OriginalInput1'] = original?.input1
        ? deepClone(original?.input1)
        : original.input1;
      context['$$OriginalInput2'] = original?.input2
        ? deepClone(original?.input2)
        : original.input2;
    }

    const modifiedExpr = this.transpileExpression(expression, false);

    const result: { value: any; error?: any } = { value: undefined };
    let hasEvalError = false;

    // When @@ is parsed by the eval, it thows "Invalid or unexpected token". For the purpose
    // of evaluating the expression, we copy the @@variables to non-@@ variables and modify
    // expression itself to not include @@ (only for context.storedVars).
    const storedVarsContext = { ...context.storedVars } as { [key: string]: any };
    for (const varName in storedVarsContext) {
      if (varName.startsWith('@@')) {
        const tempVarName = varName.replaceAll('@@', '');
        storedVarsContext[tempVarName] = storedVarsContext[varName];
        delete storedVarsContext[varName];
      }
    }
    const evalContext = { ...context, storedVars: storedVarsContext };

    try {
      Function(`
        "use strict";
        return (function(context, result) {
          try {
            result['value'] = ${modifiedExpr};
          } catch(err) {
            result['error'] = err;
          }
        })`)()(evalContext, result);
      if (result.error) {
        throw result.error;
      }
    } catch (err: any) {
      console.log(
        `Error parsing expression [${String(modifiedExpr)}].`,
        'message' in err ? err.message : err,
      );
      hasEvalError = true;
      result['value'] = undefined;
    }

    if (
      hasEvalError &&
      typeof originalExpression === 'string' &&
      isTagId(originalExpression) &&
      !this.canEvaluate(originalExpression)
    ) {
      return this.schemaService.replaceTagIdsWithValues(originalExpression);
    }

    // BL-9807: TagId expressions can sometimes fail to throw an expected error during evaluation.
    // In such cases, we need to replace the TagId with its value and return it to the caller.
    if (
      !hasEvalError &&
      typeof originalExpression === 'string' &&
      isTagId(originalExpression) &&
      result.value === undefined
    ) {
      return this.schemaService.replaceTagIdsWithValues(originalExpression);
    }

    // A simple string without additional quotes have been passed. Returning original expression.
    if (
      typeof originalExpression === 'string' &&
      originalExpression !== '' &&
      !this.canEvaluate(originalExpression) &&
      result.value === undefined
    ) {
      return originalExpression;
    }
    return result.value;
  }

  getAggregateFunctionName(fnExpr: string): AggregateFunctionName | null {
    const fnExpression = fnExpr?.toLowerCase();
    if (fnExpression.startsWith('array')) {
      return 'ARRAY';
    }
    if (fnExpression.startsWith('avg')) {
      return 'AVG';
    }
    if (fnExpression.startsWith('count')) {
      return 'COUNT';
    }
    if (fnExpression.startsWith('max')) {
      return 'MAX';
    }
    if (fnExpression.startsWith('median')) {
      return 'MEDIAN';
    }
    if (fnExpression.startsWith('min')) {
      return 'MIN';
    }
    if (fnExpression.startsWith('sum')) {
      return 'SUM';
    }
    return null;
  }

  /**
   * Parses the string value from the condition into appropriate primitive. We assume that strings specified as
   * the condition must be in quotes.
   */
  parseExpressionValue(value: string): string | number | boolean {
    value = value.trim();
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    }
    if (value.indexOf("'") >= 0 || value.indexOf('"') >= 0) {
      return value.replace(/['"]/gi, '');
    }
    // This has to be at the end to avoid false positives caused by JS parsing (ie. Number.parseFloat('1A') evals to 1)
    return Number.parseFloat(value);
  }

  normalizeLinkedParams(context: RuntimeContext): RuntimeContext {
    const normalized: any = {};

    for (const [name, value] of Object.entries(context)) {
      if (!value) {
        normalized[name] = value;
        continue;
      } else if (isDatasetLinkValue(value)) {
        // Found linked record. Make it equal rowId.
        normalized[name] = value?.['rowId'];
      } else if (Array.isArray(value)) {
        if (value.length && isDatasetLinkValue(value[0])) {
          normalized[name] = value.map((objItem) => objItem.rowId);
        } else {
          normalized[name] = value.map((objItem) =>
            !objItem || isPrimitive(objItem) ? objItem : this.normalizeLinkedParams(objItem),
          );
        }
      } else if (typeof value === 'object' && Object.keys(value)?.length) {
        normalized[name] = this.normalizeLinkedParams(value);
      } else {
        normalized[name] = deepClone(value);
      }
    }

    return normalized;
  }
  /** Splits the expression into condition tokens */

  tokenizeExpression(conditionExpr: string): string[][] | null {
    if (!conditionExpr) {
      return null;
    }

    // Order of operators in this list matters. Operators with largest amount of characters should be parsed first.
    const operator = this.operators.find(
      (supportedOperator) => conditionExpr.indexOf(supportedOperator) >= 0,
    );
    if (!operator) {
      console.error(`Invalid condition: ${conditionExpr}. Supported operator not found.`);
      return null;
    }

    const tokens = conditionExpr
      .split(this.expressionOperatorsRx)
      .map((ex) => ex.split(this.operatorsRx).map((t) => t.trim().replace(/[()]/g, '')));

    return tokens;
  }

  evalExpressionObj(
    expression: ExpressionObj[] | string | undefined,

    context: any,
    ignoreNormalization: boolean,
  ): any {
    if (typeof expression === 'string' || !expression?.[0]?.operator) {
      return this.eval(expression, context, ignoreNormalization);
    }
    if (Array.isArray(expression)) {
      // Check if internal expressions are valid
      // They have the highest priority
      const internalConditionsFulfilled = expression
        .filter((expr) => !!expr.internalState)
        .every((expr) => this.compileExpressionObjWithContext(expr, context));

      if (!internalConditionsFulfilled) {
        return false;
      }

      const userExpressions = expression.filter((expr) => !!expr.state);

      // No expressions left to evaluate
      if (!userExpressions.length) {
        return true;
      }

      if (
        userExpressions[0].state === 'show' &&
        (!userExpressions[1] || userExpressions[1].state === 'and')
      ) {
        return userExpressions.every((expr) => this.compileExpressionObjWithContext(expr, context));
      } else if (userExpressions[0].state === 'show' && userExpressions[1]?.state === 'or') {
        return !!userExpressions.find((expr) =>
          this.compileExpressionObjWithContext(expr, context),
        );
      } else if (
        userExpressions[0].state === 'hide' &&
        (!userExpressions[1] || userExpressions[1].state === 'and')
      ) {
        return !userExpressions.every((expr) =>
          this.compileExpressionObjWithContext(expr, context),
        );
      } else {
        // expression[0].state === 'hide' && expression[1].state === 'or'
        return !userExpressions.find((expr) => this.compileExpressionObjWithContext(expr, context));
      }
    }

    return false;
  }

  /**
   * Check if expression evaluates to value
   * @param value to test agains (also implicit default)
   * @param expression like visibility expression
   * @param context evaluation context
   * @returns true iff expression evaluates to value or expression is empty
   */
  queryExpressionObj(
    value: VisibilityExpressionState,
    expression: ExpressionObj[] | undefined,

    context: any,
  ): boolean {
    const userExpressions = expression?.filter((expr) => !!expr.state);
    if (!userExpressions || userExpressions.length === 0) {
      return true;
    }
    const quantifier = userExpressions[1]?.state === 'or' ? 'some' : 'every';
    return (
      (value === userExpressions[0].state) ===
      userExpressions[quantifier]((expr) => this.compileExpressionObjWithContext(expr, context))
    );
  }

  /**
   * Creates dataset filter from logical query node. Used with dataset get v2 filter property.
   */
  private createDatasetFilterFromLogicalQueryNode(
    query: LogicalQueryNode,
    context: RuntimeContext,
  ): LogicalQueryNode {
    const root: LogicalQueryNode = {
      nodeType: QueryNodeType.Logical,
      logicalType: query.logicalType,
      children: [],
    };

    for (const child of query.children) {
      if (child.nodeType === QueryNodeType.Logical) {
        root.children.push(this.createDatasetFilterFromLogicalQueryNode(child, context));
        continue;
      }

      const comparisonNode: ComparisonQueryNode = {
        column: child.column,
        displayInfos: [],
        isFormulaValue: child.isFormulaValue,
        isOptional: child.isOptional,
        nodeType: QueryNodeType.Comparison,
        operator: child.operator,
        value: child.value,
      };

      if (comparisonOperatorsWithNoValue.has(child.operator)) {
        root.children.push(comparisonNode);
      } else if (child.isOptional) {
        const value = this.eval(child.value, context);
        if (
          (Array.isArray(value) && value?.length) ||
          (!Array.isArray(value) && (!!value || value === 0))
        ) {
          comparisonNode.value = this.prepareFilterValue(value);
          root.children.push(comparisonNode);
        }
      } else {
        comparisonNode.value = this.prepareFilterValue(this.eval(child.value, context));
        root.children.push(comparisonNode);
      }
    }

    return root;
  }

  /**
   * Creates dataset filter from condition expression. Used with dataset get v2 filter property.
   */
  private createDatasetFilterFromCondition(
    expression: string,
    logicalType: LogicalType,
    context: RuntimeContext,
  ): LogicalQueryNode {
    const node: LogicalQueryNode = {
      nodeType: QueryNodeType.Logical,
      logicalType,
      children: [],
    };

    const expressions = this.getIndividualExpressions(expression);
    if (!expression?.length) {
      return node;
    }

    expressions.forEach((expr) => {
      const exprOp = this.operators.find((operator) => expr.indexOf(operator) > 0) || '';
      const splitReg = `${exprOp}(.*)`;
      const regExp = new RegExp(splitReg, 's');
      const splitExpr = expr.split(regExp);
      const op = this.convertToComparisonOperator(exprOp);

      if (!op) {
        throw new Error(
          `${IntellisenseErrorMessage.InvalidExpression}: ${expr} (part of: ${expression})`,
        );
      }

      let comparisonNode: ComparisonQueryNode = {
        column: '',
        displayInfos: [],
        isFormulaValue: false,
        isOptional: false,
        nodeType: QueryNodeType.Comparison,
        operator: op,
        value: undefined,
      };

      splitExpr.forEach((exprPart, index) => {
        if (exprPart) {
          if (index === 0) {
            const names = exprPart
              .trim()
              .split(',')
              .map((text) => text.trim());
            comparisonNode.column = names?.[0] ?? '';
          } else {
            const value = this.eval(exprPart.trim(), context);
            const isFormulaValue = typeof value === 'string' ? isFormula(value) : false;
            if (isFormulaValue) {
              comparisonNode = { ...comparisonNode, isFormulaValue, value };
            } else {
              comparisonNode.value = this.prepareFilterValue(value);
            }
          }
        }
      });
      if (
        (comparisonNode?.value &&
          typeof comparisonNode.value === 'object' &&
          Object.keys(comparisonNode.value)?.length) ||
        (typeof comparisonNode?.value === 'undefined' &&
          (comparisonNode.operator === 'IsNull' || comparisonNode.operator === 'IsNotNull')) ||
        (comparisonNode?.value !== undefined && typeof comparisonNode?.value !== 'object')
      ) {
        node.children.push(comparisonNode);
      }
    });
    return node;
  }

  /**
   * Creates dataset filter from query conditions. Used with dataset get v2 filter property.
   */
  private createDatasetFilterFromQueryConditions(
    queryConditions: WorkflowPredicate[],
    logicalType: LogicalType,
    context: RuntimeContext,
  ): LogicalQueryNode {
    let children: (ComparisonQueryNode | LogicalQueryNode)[] = [];

    queryConditions.forEach((queryCondition) => {
      // Wrap query children in an Or logical node
      const logicalParentNode: LogicalQueryNode = {
        children: [],
        logicalType: LogicalType.Or,
        nodeType: QueryNodeType.Logical,
      };

      const hasMultipleColumns = queryCondition?.columns?.length > 1;

      // For each column in the query condition, create a comparison node and make them children
      // of the parent Or logical node.
      for (const column of queryCondition.columns) {
        const child: ComparisonQueryNode = {
          column: column,
          displayInfos: [],
          isFormulaValue: queryCondition.isFormulaValue,
          isOptional: queryCondition.isOptional,
          nodeType: QueryNodeType.Comparison,
          operator: queryCondition.operator,
          value: undefined,
        };

        if (comparisonOperatorsWithNoValue.has(queryCondition.operator)) {
          logicalParentNode.children.push(child);
        } else if (queryCondition.isOptional) {
          // Query condition is optional - don't include it in the query if it doesn't have a value assigned
          const value = this.eval(queryCondition?.value, context);
          if (
            (Array.isArray(value) && value?.length) ||
            (!Array.isArray(value) && (!!value || value === 0))
          ) {
            child.value = this.prepareFilterValue(value);
            logicalParentNode.children.push(child);
          }
        } else if (!queryCondition.isOptional) {
          // Query condition is not optional - pass whatever value we evaluated to
          child.value = this.prepareFilterValue(this.eval(queryCondition?.value, context));
          logicalParentNode.children.push(child);
        }
      }

      // If there are multiple columns, add the logical parent node to the children.
      // Otherwise, add the child to the children directly.
      if (hasMultipleColumns) {
        children.push({ ...logicalParentNode });
      } else {
        children = children.concat([...logicalParentNode.children]);
      }
    });

    return {
      children,
      logicalType,
      nodeType: QueryNodeType.Logical,
    };
  }

  /**
   * Creates dataset query from logical query node. Used with dataset get v1 query property.
   */
  private createDatasetQueryFromLogicalQueryNode(
    query: LogicalQueryNode,
    context: RuntimeContext,
  ): LogicalPredicateNode {
    const root: LogicalPredicateNode = {
      nodeType: PredicateType.Logical,
      logicalType: query.logicalType,
      children: [],
    };

    for (const child of query.children) {
      if (child.nodeType === QueryNodeType.Logical) {
        root.children.push(this.createDatasetQueryFromLogicalQueryNode(child, context));
        continue;
      }

      const predicateNode: SimplePredicateNode = {
        nodeType: PredicateType.Comparison,
        op: child.operator,
        names: [child.column],
      };

      if (comparisonOperatorsWithNoValue.has(child.operator)) {
        root.children.push(predicateNode);
      } else if (child.isOptional) {
        const value = this.eval(child.value, context);
        if (
          (Array.isArray(value) && value?.length) ||
          (!Array.isArray(value) && (!!value || value === 0))
        ) {
          predicateNode.value = value;
          root.children.push(predicateNode);
        }
      } else {
        predicateNode.value = this.eval(child.value, context);
        root.children.push(predicateNode);
      }
    }

    return root;
  }

  /**
   * Creates dataset query from condition expression. Used with dataset get v1 query property.
   */
  private createDatasetQueryFromCondition(
    expression: string,
    logicalType: LogicalType,
    context: RuntimeContext,
  ): LogicalPredicateNode {
    const node: LogicalPredicateNode = {
      nodeType: PredicateType.Logical,
      logicalType,
      children: [],
    };

    const expressions = this.getIndividualExpressions(expression);
    if (!expression?.length) {
      return node;
    }

    expressions.forEach((expr) => {
      const exprOp = this.operators.find((operator) => expr.indexOf(operator) > 0) || '';
      const splitReg = `${exprOp}(.*)`;
      const regExp = new RegExp(splitReg, 's');
      const splitExpr = expr.split(regExp);
      const op = this.convertToComparisonOperator(exprOp);

      if (!op) {
        throw new Error(
          `${IntellisenseErrorMessage.InvalidExpression}: ${expr} (part of: ${expression})`,
        );
      }

      const simplePredicateNode: SimplePredicateNode = {
        names: [],
        op: op,
        nodeType: PredicateType.Comparison,
      };

      splitExpr.forEach((exprPart, index) => {
        if (exprPart) {
          if (index === 0) {
            simplePredicateNode.names = exprPart
              .trim()
              .split(',')
              .map((text) => text.trim());
          } else {
            simplePredicateNode.value = this.eval(exprPart, context);
          }
        }
      });
      if (
        (simplePredicateNode?.value &&
          typeof simplePredicateNode.value === 'object' &&
          Object.keys(simplePredicateNode.value)?.length) ||
        (typeof simplePredicateNode?.value === 'undefined' &&
          (simplePredicateNode.op === 'IsNull' || simplePredicateNode.op === 'IsNotNull')) ||
        (simplePredicateNode?.value !== undefined && typeof simplePredicateNode?.value !== 'object')
      ) {
        node.children.push(simplePredicateNode);
      }
    });
    return node;
  }

  /**
   * Creates dataset query from query conditions. Used with dataset get v1 query property.
   */
  private createDatasetQueryFromQueryConditions(
    queryConditions: WorkflowPredicate[],
    logicalType: LogicalType,
    context: RuntimeContext,
  ): LogicalPredicateNode {
    const predicateNodes: SimplePredicateNode[] = [];
    queryConditions.forEach((queryCondition) => {
      const comparison: SimplePredicateNode = {
        names: queryCondition.columns,
        op: queryCondition.operator,
        nodeType: PredicateType.Comparison,
      };

      if (comparisonOperatorsWithNoValue.has(queryCondition.operator)) {
        predicateNodes.push(comparison);
      } else if (queryCondition.isOptional) {
        // Query condition is optional - don't include it in the query if it doesn't have a value assigned
        const value = this.eval(queryCondition?.value, context);
        if (
          (Array.isArray(value) && value?.length) ||
          (!Array.isArray(value) && (!!value || value === 0))
        ) {
          comparison.value = value;
          predicateNodes.push(comparison);
        }
      } else if (!queryCondition.isOptional) {
        // Query condition is not optional - pass whatever value we evaluated to
        comparison.value = this.eval(queryCondition?.value, context);
        predicateNodes.push(comparison);
      }
    });

    return {
      nodeType: PredicateType.Logical,
      children: predicateNodes,
      logicalType,
    };
  }

  /**
   * Recursively evalates any nested expression.
   * @param expression
   * @param context
   * @returns
   */
  private evalAny(expression: string | any, context: RuntimeContext): any {
    // Expression acan be a string but it also can be a nested object.
    let evaluated: string | any = null;

    if (Array.isArray(expression)) {
      evaluated = [];
      for (let i = 0; i < expression?.length; i++) {
        evaluated[i] = this.evalAny(expression[i], context);
      }
    } else if (expression && typeof expression === 'object' && !!Object.keys(expression).length) {
      evaluated = {};
      for (const key in expression) {
        evaluated[key] = this.evalAny(expression[key], context);
      }
    } else if (this.canEvaluate(expression)) {
      evaluated = this.eval(expression as string, context);
    } else if (isTagId(expression)) {
      evaluated = this.schemaService.replaceTagIdsWithValues(expression);
    } else {
      evaluated = expression;
    }
    return evaluated;
  }

  private convertToComparisonOperator(op: string): ComparisonOperator | null {
    switch (op) {
      case '!=':
        return ComparisonOperator.Neq;
      case '!==':
        return ComparisonOperator.Neq;
      case '<':
        return ComparisonOperator.Lt;
      case '<=':
        return ComparisonOperator.Lte;
      case '=':
        return ComparisonOperator.Eq;
      case '==':
        return ComparisonOperator.Eq;
      case '===':
        return ComparisonOperator.Eq;
      case '>':
        return ComparisonOperator.Gt;
      case '>=':
        return ComparisonOperator.Gte;
      case 'ContainsText':
        return ComparisonOperator.ContainsText;
      case 'HasPrefix':
        return ComparisonOperator.HasPrefix;
      case 'HasPrefixIgnoreCase':
        return ComparisonOperator.HasPrefixIgnoreCase;
      case 'IsFalse':
        return ComparisonOperator.IsFalse;
      case 'IsNotNull':
        return ComparisonOperator.IsNotNull;
      case 'IsNull':
        return ComparisonOperator.IsNull;
      case 'IsTrue':
        return ComparisonOperator.IsTrue;
      case 'Near':
        return ComparisonOperator.Near;
      default:
        return null;
    }
  }

  private getIndividualExpressions(conditionsExpr = ''): string[] {
    return conditionsExpr?.trim().split('&&');
  }

  /**
   * Removes known red-flags from the js expression.
   */
  private sanitizeExpression(expression: string): string {
    return expression.replaceAll('<script>', '');
  }

  private compileExpressionObjWithContext(
    expressionObj: ExpressionObj,
    context: any,
  ): boolean | undefined {
    const candidatePairs = this.getValueCandidatePairs(expressionObj, context);
    const operator = this.getCondition(expressionObj.operator, expressionObj.columnsDataset);
    const durationFormat =
      expressionObj.columnsDataset?.dataType === 'Duration'
        ? expressionObj.columnsDataset.format?.durationFormat
        : false;

    const evaluate = ([col, val]: [any, any]): boolean | undefined =>
      evaluateExpression(operator, col, val, durationFormat);

    const negativeOperators: string[] = [
      ComparisonOperator.Neq,
      ComparisonOperator.NotIn,
      'DoesNotContain',
    ];
    return negativeOperators.includes(operator)
      ? candidatePairs.every((pair) => evaluate(pair) === true)
      : candidatePairs.some((pair) => evaluate(pair) === true);
  }

  private getValueCandidatePairs(expression: ExpressionObj, context: any): Array<[any, any]> {
    const isUnaryOperator = this.unaryOperators.includes(expression.operator);

    if (!isUnaryOperator && isNullish(expression.value)) {
      throw new Error(
        `Invalid expression: ${expression.columns} ${expression.operator} ${expression.value}`,
      );
    }

    const colExpr = this.transpileExpression(expression.columns);

    const val = this.resolveExpressionValue(expression, context);
    const col = this.resolveTypedValue(colExpr, expression.columnsDataset, context, expression);

    const isLinkLikeColumn =
      expression.columnsDataset?.columnType === 'Link' ||
      (expression.columnsDataset && isLinkedInlineColumnAsLink(expression.columnsDataset));
    const isLinkStringComparison =
      isLinkLikeColumn && !expression.valueDataset && typeof val === 'string';

    if (isLinkStringComparison) {
      const rawLinkObject = this.resolveTypedValue(colExpr, undefined, context, expression);
      const displayValue = this.resolveLinkDisplayValue(rawLinkObject);
      if (displayValue !== undefined) {
        return [
          [col, val],
          [displayValue, this.normalizeQuotedString(val)],
          [displayValue, val.split(',').map((v) => this.normalizeQuotedString(v))],
        ];
      }
    }

    return [[col, val]];
  }

  private resolveExpressionValue(expression: ExpressionObj, context: any): any {
    const rawValue = expression.value;
    const valueDataset = expression.valueDataset;
    const isUnaryOperator = this.unaryOperators.includes(expression.operator);

    if (
      typeof rawValue === 'string' &&
      !rawValue.startsWith('[') &&
      [ComparisonOperator.In, ComparisonOperator.NotIn].includes(expression.operator) &&
      expression.columnsDataset?.tagDefinition?.items?.length
    ) {
      const parts = rawValue.split(',').map((part: string) => part.trim());
      const tagValues = new Set(
        expression.columnsDataset.tagDefinition.items.map((item) => item.value),
      );
      if (parts.every((part: string) => tagValues.has(part))) {
        return parts;
      }
    }

    const expr = isUnaryOperator ? rawValue : this.transpileExpression(rawValue as any);
    const isInlineJsonArray = !valueDataset && expr?.startsWith?.('[');

    if (!isInlineJsonArray) {
      return this.resolveTypedValue(expr, valueDataset, context, expression);
    }
    try {
      const stripWrappingQuotes = /^"|"$/g;
      const items: string[] = JSON.parse(expr ?? '');
      return items
        .map((item) => item.replaceAll?.(stripWrappingQuotes, '') || item)
        .map((item) => this.resolveTypedValue(item, valueDataset, context, expression));
    } catch {
      console.error(`not expected type for condition value: ${expr}`);
      return this.resolveTypedValue(expr, valueDataset, context, expression);
    }
  }
  private resolveTypedValue(
    value: any,
    dataset: DatasetColumn | undefined,
    context: any,
    expression: ExpressionObj,
  ): any {
    if (value?.startsWith('context.')) {
      const path = toPath(value.replace('context.', '').replaceAll('?.', '.'));
      value = path.reduce((prev, current) => prev?.[current], context);
    }
    // if value is scope -> we need to be precise about value
    if (this.isVariableInfo(value)) {
      value = value.value;
    }
    if (dataset?.columnType === 'Tag') {
      value = value?.display || value?.value || value;
    }
    if (
      dataset?.columnType === 'Link' ||
      (dataset?.columnType === 'Formula' && isDatasetLinkValueOrArrayOfLinkValues(value))
    ) {
      if (
        expression.operator === ComparisonOperator.Eq ||
        expression.operator === ComparisonOperator.Neq ||
        (expression.operator === ComparisonOperator.NotIn &&
          expression.columnsDataset?.multipleValues)
      ) {
        value = Array.isArray(value) ? value.map((v: any) => v.rowId || v) : value?.rowId || value;
      } else {
        value = Array.isArray(value)
          ? value.map((v: any) => v.display || v)
          : value?.display || value;
      }
    }
    // value fix when column format is eq PhoneNumber
    if (expression.columnsDataset?.format?.formatType === 'PhoneNumber') {
      const browserCountry = userBrowserCountry() as CountryCode;
      value = isPossiblePhoneNumber(value || '', browserCountry)
        ? parsePhoneNumberWithError(value || '', browserCountry).nationalNumber
        : value;
    } else if (expression.columnsDataset?.dataType === 'Date') {
      value = value?.split?.('T')?.[0] || value;
    } else if (
      expression.columnsDataset?.format?.formatType === 'Duration' &&
      expression.columnsDataset.format.durationFormat &&
      !isDurationInSaveFormat(value)
    ) {
      value =
        formatDurationForSaving(value || '', expression.columnsDataset.format.durationFormat) ||
        value;
    } else if (expression.columnsDataset?.dataType === 'GeoPoint') {
      value = isGeoPoint(value)
        ? value?.coordinates.join()
        : (value && createGeoPointFromString(value)?.coordinates.join()) || '';
    }
    return value;
  }

  private transpileExpression(expr: string, keepAtAt = true): string {
    const PARAMS_WITH_PROTECTED_$$ = [
      'Account',
      'OriginalInput1',
      'OriginalInput2',
      'Page',
      'Url',
      'User',
    ];

    const storedVarsReplacement = keepAtAt ? `context.storedVars.@@` : 'context.storedVars.';
    let expression = this.schemaService.replaceColumnAndTagIds(
      normalizeQuotes(expr.replaceAll('@@', storedVarsReplacement))
        .replaceAll('“', '"')
        .replaceAll('”', '"'),
    );

    // Temporarily replace $$ in protected params. They will be handled separately.. Order of these expressions matter.
    for (const protectedParam of PARAMS_WITH_PROTECTED_$$) {
      expression = expression.replaceAll(`$$${protectedParam}[`, `__bracket__${protectedParam}[`);
      expression = expression.replaceAll(
        `$$${protectedParam}?.`,
        `__question__${protectedParam}?.`,
      );
      expression = expression.replaceAll(`$$${protectedParam}.`, `__dot__${protectedParam}.`);
    }

    // For the rest of the params, replace $$ with context.
    expression = expression.replaceAll(`$$`, `context.`);

    // Properly replace protected params prefix
    for (const protectedParam of PARAMS_WITH_PROTECTED_$$) {
      expression = expression.replaceAll(
        `__bracket__${protectedParam}[`,
        `context.$$$$${protectedParam}[`,
      );
      expression = expression.replaceAll(
        `__question__${protectedParam}?.`,
        `context.$$$$${protectedParam}?.`,
      );
      expression = expression.replaceAll(
        `__dot__${protectedParam}.`,
        `context.$$$$${protectedParam}.`,
      );
    }

    return this.sanitizeExpression(expression);
  }

  private getCondition(condition: ComparisonOperator, columnsDataset?: DatasetColumn): string {
    const columnType = columnsDataset?.columnType;

    const isSupportedFormulaType =
      columnsDataset && columnType === 'Formula' && isLinkedInlineColumnAsLink(columnsDataset);

    if (
      (columnType === 'Link' || columnType === 'Tag' || isSupportedFormulaType) &&
      columnsDataset?.multipleValues &&
      [ComparisonOperator.Eq, ComparisonOperator.NotIn].includes(condition)
    ) {
      return columnsDataset?.multipleValues
        ? condition === ComparisonOperator.Eq
          ? 'ContainsEvery'
          : 'DoesNotContain'
        : condition === ComparisonOperator.Eq
          ? ComparisonOperator.ContainsText
          : ComparisonOperator.NotIn;
    }
    return condition;
  }

  private isVariableInfo(value: any): value is VariableInfo {
    return (
      !!value?.scope && 'name' in value && 'id' in value && 'projectId' in value && 'value' in value
    );
  }

  /**
   * Formats value to be used in dataset filter.
   * @param value
   * @returns - Values passed to the dataset filter have to be stringified.
   */
  private prepareFilterValue(value: any): string | undefined {
    if (isNullish(value)) {
      return undefined;
    }
    if (typeof value === 'string') {
      return value;
    }
    return JSON.stringify(value);
  }

  private resolveLinkDisplayValue(value: unknown): string | string[] | undefined {
    if (Array.isArray(value)) {
      const displays = value
        .map((v) => v?.display)
        .filter((d): d is string => typeof d === 'string');
      return displays.length > 0 ? displays : undefined;
    }
    if (value && typeof value === 'object' && 'display' in value) {
      return value.display as string | undefined;
    }
    return undefined;
  }

  private normalizeQuotedString(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }
    return value.replaceAll(/^['"]|['"]$/g, '');
  }
}
